import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Fuel, Send, RefreshCw, Loader2, AlertTriangle, CheckCircle, ExternalLink, Wallet } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminSendEthCard = ({ token }) => {
  const [subscribers, setSubscribers] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [fundingWallet, setFundingWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState({});
  const [customAmount, setCustomAmount] = useState({});

  useEffect(() => {
    loadData();
  }, [token]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [subsRes, transfersRes, walletRes] = await Promise.all([
        axios.get(`${API}/pas-eth/admin/subscribers-needing-gas`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/pas-eth/admin/transfers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/pas-eth/admin/funding-wallet`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null }))
      ]);
      setSubscribers(subsRes.data.subscribers || []);
      setTransfers(transfersRes.data.transfers || []);
      setFundingWallet(walletRes.data);
    } catch (err) {
      console.error('Failed to load data:', err);
      toast.error('Failed to load subscriber data');
    } finally {
      setLoading(false);
    }
  };

  const sendEth = async (sub, amount = 0.01) => {
    const ethAmount = customAmount[sub.client_id] || amount;
    if (ethAmount <= 0 || ethAmount > 1) {
      toast.error('Amount must be between 0 and 1 ETH');
      return;
    }

    setSending(s => ({ ...s, [sub.client_id]: true }));
    try {
      const res = await axios.post(`${API}/pas-eth/admin/send-eth`, {
        client_id: sub.client_id,
        eth_amount: parseFloat(ethAmount),
        reason: 'Gas funding for Treasury Check deployment'
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success(res.data.message);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send ETH');
    } finally {
      setSending(s => ({ ...s, [sub.client_id]: false }));
    }
  };

  const needsGasSubs = subscribers.filter(s => s.needs_gas === true);
  const fundedSubs = subscribers.filter(s => s.needs_gas === false);

  if (loading) {
    return (
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 text-blue-400 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden" data-testid="admin-send-eth-card">
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Fuel className="w-8 h-8 text-white" />
            <div>
              <h3 className="text-lg font-bold text-white">Gas Funding</h3>
              <p className="text-sm text-blue-100">Send ETH to subscribers for deployments</p>
            </div>
          </div>
          <button onClick={loadData} className="p-2 text-white/70 hover:text-white">
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Funding Wallet Status */}
        {fundingWallet && (
          <div className={`rounded-lg p-4 ${fundingWallet.can_send ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
            <div className="flex items-center gap-3">
              <Wallet className={`w-5 h-5 ${fundingWallet.can_send ? 'text-emerald-400' : 'text-red-400'}`} />
              <div className="flex-1">
                <p className="text-sm font-medium text-white">Your Funding Wallet</p>
                <p className="text-xs font-mono text-slate-400">{fundingWallet.address}</p>
              </div>
              {fundingWallet.configured && fundingWallet.balance_eth !== null && (
                <div className="text-right">
                  <p className="text-lg font-bold text-emerald-400">{fundingWallet.balance_eth} ETH <span className="text-xs text-slate-400">(Ethereum)</span></p>
                  {fundingWallet.balance_usd !== null && (
                    <p className="text-sm text-emerald-300">${fundingWallet.balance_usd.toLocaleString()}</p>
                  )}
                  {fundingWallet.eth_price_usd && (
                    <p className="text-xs text-slate-500">@ ${fundingWallet.eth_price_usd.toLocaleString()}/ETH</p>
                  )}
                </div>
              )}
            </div>
            {fundingWallet.base && (
              <div className="mt-3 grid grid-cols-3 gap-2 text-center" data-testid="funding-wallet-base-holdings">
                {[['BETH USD (Official)', fundingWallet.base.beth_usd, 'beth-usd'], ['ETH on Base', fundingWallet.base.eth, 'eth'], ['USDC on Base', fundingWallet.base.usdc, 'usdc']].map(([l, v, id]) => (
                  <div key={id} className="bg-slate-900/60 rounded-lg p-2"><p className="text-[11px] text-slate-400">{l}</p>
                    <p className="font-semibold text-white" data-testid={`funding-wallet-base-${id}`}>{v == null ? '-' : Number(v).toLocaleString(undefined, { maximumFractionDigits: 6 })}</p></div>
                ))}
              </div>
            )}
            {fundingWallet.base?.gas_low && (
              <p className="mt-2 text-xs text-amber-300" data-testid="funding-wallet-base-gas-low">No ETH on Base in this wallet, so it can't pay network fees to move or swap its BETH USD yet.</p>
            )}
            {!fundingWallet.configured && (
              <p className="mt-2 text-xs text-red-300">Funding wallet key not configured. Add ADMIN_FUNDING_WALLET_KEY to enable sending.</p>
            )}
            {fundingWallet.configured && fundingWallet.key_problem && (
              <p className="mt-3 flex items-start gap-2 text-sm font-medium text-red-300" data-testid="funding-wallet-key-problem"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />{fundingWallet.key_problem}</p>
            )}
            {fundingWallet.can_send && fundingWallet.balance_eth !== null && (
              <p className="mt-2 text-xs text-slate-400" data-testid="funding-wallet-max-send">
                Max you can send now: {Math.max(fundingWallet.balance_eth - (fundingWallet.send_reserve_eth || 0), 0).toFixed(6)} ETH
                ({fundingWallet.send_reserve_eth} ETH is kept for network fees)
              </p>
            )}
          </div>
        )}

        {/* Subscribers Needing Gas */}
        {needsGasSubs.length > 0 && (
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-amber-400 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> Subscribers Needing Gas ({needsGasSubs.length})
            </h4>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {needsGasSubs.map(sub => (
                <div key={sub.client_id} className={`rounded-lg p-3 ${sub.gas_level === 'critical' ? 'bg-red-500/10 border border-red-500/30' : 'bg-amber-500/10 border border-amber-500/30'}`}>
                  <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <p className="text-white font-medium truncate">{sub.full_name || sub.email}</p>
                      <p className="text-xs text-slate-400 truncate">{sub.email}</p>
                      <p className="text-xs text-slate-500 font-mono truncate">{sub.wallet_address}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-sm font-medium ${sub.gas_level === 'critical' ? 'text-red-400' : 'text-amber-300'}`}>
                          {sub.eth_balance !== null ? `${sub.eth_balance} ETH` : '—'}
                        </span>
                        {sub.usd_value !== null && (
                          <span className="text-xs text-slate-400">(${sub.usd_value.toFixed(2)})</span>
                        )}
                        {sub.gas_level === 'critical' && (
                          <span className="px-1.5 py-0.5 bg-red-500/20 text-red-300 text-xs rounded">CRITICAL</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <input type="number" 
                        value={customAmount[sub.client_id] || ''}
                        onChange={(e) => setCustomAmount(a => ({ ...a, [sub.client_id]: e.target.value }))}
                        placeholder="0.01"
                        step="0.001" min="0.001" max="1"
                        className="w-20 px-2 py-1 bg-slate-700 border border-slate-600 rounded text-white text-sm"
                      />
                      <button onClick={() => sendEth(sub)}
                        disabled={sending[sub.client_id] || fundingWallet?.can_send === false}
                        className="flex items-center gap-1 px-3 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded text-sm disabled:opacity-50">
                        {sending[sub.client_id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                        Send
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Funded Subscribers */}
        {fundedSubs.length > 0 && (
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-emerald-400 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" /> Funded Subscribers ({fundedSubs.length})
            </h4>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {fundedSubs.slice(0, 10).map(sub => (
                <div key={sub.client_id} className="bg-slate-700/30 rounded-lg p-2 flex items-center justify-between text-sm">
                  <span className="text-slate-300 truncate">{sub.full_name || sub.email}</span>
                  <div className="text-right">
                    <span className="text-emerald-400">{sub.eth_balance} ETH</span>
                    {sub.usd_value !== null && (
                      <span className="text-slate-500 text-xs ml-1">(${sub.usd_value.toFixed(2)})</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Transfers */}
        {transfers.length > 0 && (
          <div className="space-y-3 border-t border-slate-700 pt-6">
            <h4 className="text-sm font-medium text-slate-300">Recent Transfers</h4>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {transfers.slice(0, 10).map(t => (
                <div key={t.transfer_id} className="bg-slate-700/30 rounded-lg p-2 flex items-center justify-between text-sm">
                  <div>
                    <span className="text-slate-300">{t.client_email}</span>
                    <span className="text-emerald-400 ml-2">{t.eth_amount} ETH</span>
                  </div>
                  <a href={t.explorer_url} target="_blank" rel="noopener noreferrer"
                    className="text-blue-400 hover:text-blue-300">
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}

        {subscribers.length === 0 && (
          <div className="text-center py-8 text-slate-400">
            <p>No subscribers with linked wallets yet</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSendEthCard;
